import {
  FETCH_ADMIN_MESSAGES,
  FETCH_ADMIN_MESSAGES_SUCCESS,
  FETCH_ADMIN_MESSAGES_FAILED,
  SEND_ADMIN_MESSAGE,
  STOP_FETCH_ADMIN_MESSAGES,
  FETCH_ALL_SUPPORT_CHATS,
  FETCH_ALL_SUPPORT_CHATS_SUCCESS,
  FETCH_ALL_SUPPORT_CHATS_FAILED
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { RequestPushMsg } from '../other/NotificationFunctions';
import { onValue, push, off, child, orderByChild, query } from "firebase/database";

// Fetch messages for a specific user-admin chat
export const fetchAdminChatMessages = (userId) => (dispatch) => {
  const {
    adminSupportRef
  } = firebase;

  dispatch({
    type: FETCH_ADMIN_MESSAGES,
    payload: userId,
  });

  // Reduce timeout for better user experience
  const timeoutId = setTimeout(() => {
    dispatch({
      type: FETCH_ADMIN_MESSAGES_SUCCESS,
      payload: [],
    });
  }, 3000); // Reduced from 5 seconds to 3 seconds

  onValue(adminSupportRef(userId), snapshot => {
    clearTimeout(timeoutId);

    if (snapshot.val()) {
      let rootEntry = snapshot.val();
      let allMessages = [];
      for (let key in rootEntry) {
        rootEntry[key].msgId = key;
        allMessages.push(rootEntry[key]);
      }
      // Sort by createdAt
      allMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      dispatch({
        type: FETCH_ADMIN_MESSAGES_SUCCESS,
        payload: allMessages,
      });
    } else {
      // Path exists but is empty, return empty array
      dispatch({
        type: FETCH_ADMIN_MESSAGES_SUCCESS,
        payload: [],
      });
    }
  }, (error) => {
    clearTimeout(timeoutId);
    console.log("Firebase error:", error);
    dispatch({
      type: FETCH_ADMIN_MESSAGES_SUCCESS,
      payload: [],
    });
  });
};

// Send message in admin support chat
export const sendAdminMessage = (data) => (dispatch) => {
  const {
    adminSupportRef,
    singleUserRef,
    usersRef
  } = firebase;

  var today = new Date();
  var time = today.getHours() + ":" + today.getMinutes();
  var dd = String(today.getDate()).padStart(2, '0');
  var mm = String(today.getMonth() + 1).padStart(2, '0');
  var yyyy = today.getFullYear();
  var dateString = mm + ':' + dd + ':' + yyyy;

  const msg = {
    message: data.message,
    from: data.from, // userId or 'admin'
    fromType: data.fromType, // 'user' or 'admin'
    userType: data.userType, // 'customer' or 'driver' 
    msgDate: dateString,
    msgTime: time,
    createdAt: new Date().toISOString(),
    read: false,
    // Add user information
    userName: data.userName || (data.fromType === 'admin' ? 'Admin' : 'User'),
    userImage: data.userImage || null,
    // For admin messages, we can add admin info
    adminName: data.fromType === 'admin' ? (data.adminName || 'Admin') : null,
    adminImage: data.fromType === 'admin' ? (data.adminImage || null) : null
  };

  // Send to Firebase
  push(adminSupportRef(data.userId), msg);

  // Send push notification
  if (data.fromType === 'admin' && data.userToken) {
    console.log('🔔 Sending push notification to user:', {
      userToken: data.userToken,
      userId: data.userId,
      message: data.message
    });

    RequestPushMsg(
      data.userToken,
      {
        title: store.getState().languagedata.defaultLanguage.notification_title,
        msg: store.getState().languagedata.defaultLanguage.admin_support_reply + ": " + data.message,
        screen: 'AdminSupport',
        params: { userId: data.userId }
      }
    );
  } else if (data.fromType === 'user') {
    console.log('🔔 User sent message, notifying admins...');

    // Get admin tokens and notify all admin users
    onValue(usersRef, snapshot => {
      if (snapshot.val()) {
        const users = snapshot.val();
        const adminTokens = [];

        // Find all admin users and collect their push tokens
        Object.keys(users).forEach(uid => {
          const user = users[uid];
          if (user.usertype === 'admin' && user.pushToken && user.pushToken !== 'init') {
            adminTokens.push(user.pushToken);
          }
        });

        console.log('🔔 Found admin tokens:', adminTokens.length);

        // Send notifications to all admin users
        adminTokens.forEach(token => {
          RequestPushMsg(
            token,
            {
              title: store.getState().languagedata.defaultLanguage.notification_title,
              msg: `${data.userType} ${store.getState().languagedata.defaultLanguage.support_request}: ${data.message}`,
              screen: 'AdminSupportList',
              params: { userId: data.userId }
            }
          );
        });
      }
    }, { onlyOnce: true });
  } else {
    console.log('⚠️ No push notification sent - missing conditions:', {
      fromType: data.fromType,
      userToken: data.userToken,
      hasUserToken: !!data.userToken
    });
  }

  // Only dispatch to Redux for optimistic UI update
  // The real-time listener will handle the actual update
  // Comment out this dispatch to prevent double messages
  // dispatch({
  //   type: SEND_ADMIN_MESSAGE,
  //   payload: msg,
  // });
};

// Stop fetching messages for admin support
export const stopFetchAdminMessages = (userId) => (dispatch) => {
  const {
    adminSupportRef
  } = firebase;

  dispatch({
    type: STOP_FETCH_ADMIN_MESSAGES,
    payload: userId,
  });
  off(adminSupportRef(userId));
};

// Fetch all support chats for admin dashboard
export const fetchAllSupportChats = () => (dispatch) => {
  const {
    adminSupportListRef
  } = firebase;

  dispatch({
    type: FETCH_ALL_SUPPORT_CHATS,
  });

  // Reduce timeout for better user experience
  const timeoutId = setTimeout(() => {
    dispatch({
      type: FETCH_ALL_SUPPORT_CHATS_SUCCESS,
      payload: [],
    });
  }, 3000); // Reduced from 5 seconds to 3 seconds

  onValue(adminSupportListRef, snapshot => {
    clearTimeout(timeoutId); // Clear timeout since we got a response

    if (snapshot.val()) {
      let allChats = [];
      let chatData = snapshot.val();

      for (let userId in chatData) {
        let messages = Object.values(chatData[userId]);
        if (messages && messages.length > 0) {
          // Sort messages by creation time
          messages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

          let lastMessage = messages[messages.length - 1];
          let unreadCount = messages.filter(msg => !msg.read && msg.fromType === 'user').length;

          // Get userType from the first user message (not admin message) to keep it consistent
          let userType = 'customer'; // default
          const firstUserMessage = messages.find(msg => msg.fromType === 'user');
          if (firstUserMessage && firstUserMessage.userType) {
            userType = firstUserMessage.userType;
          }

          allChats.push({
            userId: userId,
            lastMessage: lastMessage,
            allMessages: messages, // Include all messages for consistent user name lookup
            unreadCount: unreadCount,
            userType: userType // Use userType from first user message, not last message
          });
        }
      }

      // Sort by last message time
      allChats.sort((a, b) => new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt));

      dispatch({
        type: FETCH_ALL_SUPPORT_CHATS_SUCCESS,
        payload: allChats,
      });
    } else {
      // Path exists but is empty, return empty array
      dispatch({
        type: FETCH_ALL_SUPPORT_CHATS_SUCCESS,
        payload: [],
      });
    }
  }, (error) => {
    clearTimeout(timeoutId);
    console.log("Firebase error:", error);
    dispatch({
      type: FETCH_ALL_SUPPORT_CHATS_SUCCESS,
      payload: [],
    });
  });
};

// Mark messages as read
export const markAdminMessagesAsRead = (userId, currentUserType) => (dispatch) => {
  const {
    adminSupportRef
  } = firebase;

  // Get current messages and mark unread ones as read
  onValue(adminSupportRef(userId), snapshot => {
    if (snapshot.val()) {
      let rootEntry = snapshot.val();
      let updates = {};

      for (let key in rootEntry) {
        const message = rootEntry[key];
        // Mark user messages as read when admin opens the chat
        if (currentUserType === 'admin' && message.fromType === 'user' && !message.read) {
          updates[`${key}/read`] = true;
        }
        // Mark admin messages as read when user opens the chat
        else if (currentUserType === 'user' && message.fromType === 'admin' && !message.read) {
          updates[`${key}/read`] = true;
        }
      }

      // Update Firebase with read status
      if (Object.keys(updates).length > 0) {
        const { database } = firebase;
        const { update, ref } = require('firebase/database');
        update(ref(database, `adminSupport/${userId}`), updates)
          .then(() => {
            console.log('Messages marked as read successfully');
          })
          .catch((error) => {
            console.error('Error marking messages as read:', error);
          });
      }
    }
  }, { onlyOnce: true }); // Only run once to mark as read
}; 